import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  Check,
  FileText,
  Loader2,
  Mic,
  PenLine,
} from "lucide-react";
import { useSearchParams, useNavigate } from "react-router-dom";

import BrandStudioShellV2 from "@/components/brandStudio/BrandStudioShellV2";
import BrandStudioSignature from "@/components/brandStudio/BrandStudioSignature";
import BrandUnderstandingStepV2 from "@/components/brandStudio/BrandUnderstandingStepV2";
import BrandBusinessDataStepV2 from "@/components/brandStudio/BrandBusinessDataStepV2";
import BrandNeedsStepV2 from "@/components/brandStudio/BrandNeedsStepV2";
import BrandReferencesWorkspaceV2 from "../../components/brandStudio/BrandReferencesWorkspaceV2";
import VisionCapabilityOwnerPanelV1 from "../../components/brandStudio/VisionCapabilityOwnerPanelV1";
import VisionOrchestrationOwnerPanelV1 from "../../components/brandStudio/VisionOrchestrationOwnerPanelV1";
import VisualSynthesisCapabilityOwnerPanelV1 from "../../components/brandStudio/VisualSynthesisCapabilityOwnerPanelV1";
import VisualUnderstandingAssemblerOwnerPanelV1 from "../../components/brandStudio/VisualUnderstandingAssemblerOwnerPanelV1";
import CompactSemanticUnderstandingOwnerPanelV1 from "../../components/brandStudio/CompactSemanticUnderstandingOwnerPanelV1";
import VisualDirectionVnextFoundationOwnerPanelV1 from "../../components/brandStudio/VisualDirectionVnextFoundationOwnerPanelV1";
import VisualDirectionGenerationContractOwnerPanelV1 from "../../components/brandStudio/VisualDirectionGenerationContractOwnerPanelV1";
import VisualDirectionProviderAdapterOwnerPanelV1 from "../../components/brandStudio/VisualDirectionProviderAdapterOwnerPanelV1";
import StructuredVisualStyleOwnerPanelV1 from "../../components/brandStudio/StructuredVisualStyleOwnerPanelV1";
import VisualWorkflowPlannerOwnerPanelV1 from "../../components/brandStudio/VisualWorkflowPlannerOwnerPanelV1";
import VisualWorkflowExecutionOwnerPanelV1 from "../../components/brandStudio/VisualWorkflowExecutionOwnerPanelV1";
import VisualProviderExecutionAdapterOwnerPanelV1 from "../../components/brandStudio/VisualProviderExecutionAdapterOwnerPanelV1";
import VisualProviderOwnerExecutionPanelV1 from "../../components/brandStudio/VisualProviderOwnerExecutionPanelV1";
import VisualProviderOwnerMaterialExecutionPanelV1 from "../../components/brandStudio/VisualProviderOwnerMaterialExecutionPanelV1";
import VisualQualityGateOwnerPanelV1 from "../../components/brandStudio/VisualQualityGateOwnerPanelV1";
import VisualQualityEvaluatorPreflightOwnerPanelV1 from "../../components/brandStudio/VisualQualityEvaluatorPreflightOwnerPanelV1";
import ColorPaletteCardV1 from "../../components/brandStudio/ColorPaletteCardV1";
import BrandBoardCoreV1 from "../../components/brandStudio/BrandBoardCoreV1";
import LogoCoreStepV1 from "../../components/brandStudio/LogoCoreStepV1";
import TypographyCoreStepV1 from "../../components/brandStudio/TypographyCoreStepV1";
import ExistingBrandStartV2 from "@/components/brandStudio/ExistingBrandStartV2";
import VoiceRecorder from "@/components/voice/VoiceRecorder";
import VoiceReviewWorkspace from "@/components/voice/VoiceReviewWorkspace";
import { api, formatApiError } from "@/lib/api";
import { readBrandWorkspaceSourcesV1 } from "../../lib/brandWorkspaceReadV1";
import { assembleMyBrand } from "../../lib/myBrandPresentationV1.mjs";
import { confirmBrandStory, getLatestBrandStory } from "@/lib/brandStudioApi";

const MAX = 5000;
const VOICE_CONTEXT = {
  source_product: "brand_studio",
  purpose: "brand_story",
  context_reference: "primary",
};

export default function BrandStudioCreateV2() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const [story, setStory] = useState(null);
  const [mode, setMode] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const [localFragments, setLocalFragments] = useState([]);
  const [voiceSession, setVoiceSession] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [notice, setNotice] = useState("");
  const [resetToken, setResetToken] = useState(0);

  const start = params.get("start");
  const view = params.get("view");
  const step = params.get("step");
  const editMode = params.get("intent") === "edit";
  const [coreReady, setCoreReady] = useState(false);
  useEffect(() => {
    let cancelled=false; setCoreReady(false);
    if (!step && story) readBrandWorkspaceSourcesV1().then(data=>{if(!cancelled)setCoreReady(assembleMyBrand(data).state === "ready");}).catch(()=>{});
    return()=>{cancelled=true;};
  }, [step, story]);
  const context = params.get("context");
  const currentStart = start || (!story && mode === "voice" ? "new" : null);

  const load = async () => {
    setStatus("loading");
    setError("");
    try {
      const [storyResult, voiceResult] = await Promise.allSettled([
        getLatestBrandStory(),
        api.get("/voice/sessions/active", { params: VOICE_CONTEXT }),
      ]);

      if (storyResult.status === "rejected") throw storyResult.reason;

      const latest = storyResult.value || null;
      setStory(latest);
      setText(latest?.text || "");

      if (voiceResult.status === "fulfilled") {
        const session = voiceResult.value.data;
        setVoiceSession(session);
        setMode("voice");
        if (session.user_edited_transcript) {
          setText(session.user_edited_transcript);
          setReviewOpen(true);
          setNotice("Незавершённый рассказ восстановлен");
        }
      } else if (voiceResult.reason?.response?.status !== 404) {
        setError(formatApiError(voiceResult.reason));
      }

      setStatus("ready");
    } catch {
      setStatus("error");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const canSaveText = Boolean(text.trim()) && text.length <= MAX && !busy;
  const versionLabel = useMemo(
    () => (story ? `V${story.source_version}` : ""),
    [story],
  );

  const saveTextStory = async () => {
    if (!canSaveText) return;
    setBusy(true);
    setError("");
    try {
      const next = await confirmBrandStory({
        input_mode: "text",
        text: text.trim(),
        voice_transcript_id: null,
      });
      setStory(next);
      setText(next.text || "");
      setMode(null);
      setParams(start === "existing" ? { start: "existing" } : { view: "story" });
    } catch {
      setError("Не удалось сохранить рассказ. Текст остался на экране.");
    } finally {
      setBusy(false);
    }
  };

  const ensureVoiceSession = async () => {
    if (voiceSession) return voiceSession;
    const { data } = await api.post("/voice/sessions", VOICE_CONTEXT);
    setVoiceSession(data);
    return data;
  };

  const transcribe = async () => {
    if ((!localFragments.length && !voiceSession?.fragments?.length) || busy) {
      return;
    }
    setBusy(true);
    setError("");
    setNotice("");
    try {
      let current = await ensureVoiceSession();

      for (const fragment of localFragments) {
        const form = new FormData();
        const extension = fragment.blob.type.includes("mp4")
          ? "m4a"
          : fragment.blob.type.includes("ogg")
            ? "ogg"
            : "webm";
        form.append(
          "file",
          fragment.blob,
          `brand-story-${fragment.id}.${extension}`,
        );
        const added = await api.post(
          `/voice/sessions/${current.session_id}/fragments`,
          form,
        );
        current = added.data;
      }

      setLocalFragments([]);
      setResetToken((value) => value + 1);

      for (const fragment of current.fragments || []) {
        if (fragment.raw_transcript) continue;
        const result = await api.post(
          `/voice/sessions/${current.session_id}/fragments/${fragment.media_id}/transcribe`,
          { operation_id: crypto.randomUUID() },
        );
        current = result.data;
      }

      setVoiceSession(current);
      setText(current.user_edited_transcript || "");
      setReviewOpen(Boolean(current.user_edited_transcript));
    } catch (problem) {
      setError(formatApiError(problem));
    } finally {
      setBusy(false);
    }
  };

  const saveVoiceDraft = async () => {
    if (!voiceSession?.session_id || !text.trim()) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const { data } = await api.patch(
        `/voice/sessions/${voiceSession.session_id}/draft`,
        { text: text.slice(0, 100000) },
      );
      setVoiceSession(data);
      setText(data.user_edited_transcript);
      setNotice("Исправления сохранены");
    } catch (problem) {
      setError(formatApiError(problem));
    } finally {
      setBusy(false);
    }
  };

  const closeVoiceReview = async () => {
    if (busy) return;
    if (!voiceSession?.session_id || !text.trim()) {
      setReviewOpen(false);
      return;
    }

    setBusy(true);
    setError("");
    try {
      const { data } = await api.patch(
        `/voice/sessions/${voiceSession.session_id}/draft`,
        { text: text.slice(0, 100000) },
      );
      setVoiceSession(data);
      setText(data.user_edited_transcript || text);
      setReviewOpen(false);
    } catch (problem) {
      setError(formatApiError(problem));
    } finally {
      setBusy(false);
    }
  };

  const confirmVoiceStory = async () => {
    if (!voiceSession?.session_id || !text.trim()) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await api.patch(
        `/voice/sessions/${voiceSession.session_id}/draft`,
        { text },
      );
      const transcript = (
        await api.post(
          `/voice/sessions/${voiceSession.session_id}/confirm`,
          { explicit_confirmation: true },
        )
      ).data;
      const next = await confirmBrandStory({
        input_mode: "voice",
        text: transcript.text,
        voice_transcript_id: transcript.transcript_id,
      });
      await api.post(
        `/voice/sessions/${voiceSession.session_id}/abandon`,
      );

      setStory(next);
      setText(next.text || "");
      setVoiceSession(null);
      setLocalFragments([]);
      setReviewOpen(false);
      setNotice("");
      setMode(null);
      setResetToken((value) => value + 1);
      setParams(start === "existing" ? { start: "existing" } : { view: "story" });
    } catch (problem) {
      setError(formatApiError(problem));
    } finally {
      setBusy(false);
    }
  };

  const restartVoice = async () => {
    setBusy(true);
    setError("");
    try {
      if (voiceSession?.session_id) {
        await api.post(
          `/voice/sessions/${voiceSession.session_id}/abandon`,
        );
      }
      setVoiceSession(null);
      setLocalFragments([]);
      setText("");
      setReviewOpen(false);
      setNotice("");
      setResetToken((value) => value + 1);
      setMode("voice");
    } catch (problem) {
      setError(formatApiError(problem));
    } finally {
      setBusy(false);
    }
  };

  const selectStart = (value) => {
    setParams({ start: value });
    setMode(null);
    setError("");
  };

  const goToStep = (value) => {
    const next = new URLSearchParams(params);
    next.set("start", "new");
    next.delete("view");
    next.delete("context");
    if (value) next.set("step", value);
    else next.delete("step");
    setParams(next);
    setMode(null);
    setError("");
  };

  const openExistingStory = () => {
    const next = new URLSearchParams(params);
    next.set("start", "existing");
    next.set("context", "story");
    next.delete("step");
    next.delete("view");
    setParams(next);
    setMode(null);
    setError("");
  };

  const openTextMode = () => {
    setMode("text");
    setText(story?.text || "");
    setError("");
  };

  const openVoiceMode = () => {
    setMode("voice");
    setText("");
    setError("");
  };

  const renderTextEditor = () => (
    <div className="mt-5 rounded-[30px] border border-[#E3D5C3] bg-[#FFFDF9] p-5 md:p-8">
      <div className="flex items-center gap-2 text-[13px] text-[#765D46]">
        <FileText size={15} /> {story ? "Новая версия" : "Первый шаг"}
      </div>
      <h2 className="mt-3 font-serif text-[30px] text-[#2B211A]">
        {story ? "Изменить исходный рассказ" : "Расскажи о своём деле"}
      </h2>
      <p className="mt-2 text-[13px] leading-5 text-[#806B55]">
        Пиши своими словами. Профессиональные формулировки не нужны.
        {story ? " Предыдущая подтверждённая версия останется в истории." : ""}
      </p>
      <textarea
        value={text}
        maxLength={MAX}
        onChange={(event) => setText(event.target.value)}
        className="mt-5 min-h-[300px] w-full resize-y rounded-[20px] border border-[#DCC9AD] bg-white p-5 text-[15px] leading-7 text-[#2A1D14] outline-none focus:border-[#8C6C49]"
        placeholder="Расскажи, чем ты занимаешься, что создаёшь, для кого работаешь и что для тебя особенно важно."
        data-testid="brand-studio-story-editor-v2"
      />
      <div className="mt-2 text-right text-[11px] text-[#9A8871]">
        {text.length} / {MAX}
      </div>
      <div className="mt-5 flex flex-wrap gap-3">
        <button
          type="button"
          disabled={!canSaveText}
          onClick={saveTextStory}
          className="min-h-12 rounded-full bg-[#3D2A20] px-6 text-sm text-[#FFF8ED] disabled:opacity-45"
        >
          {busy
            ? "Сохраняю…"
            : story
              ? "Сохранить новую версию"
              : "Сохранить рассказ"}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            setMode(null);
            setText(story?.text || "");
            setError("");
          }}
          className="min-h-12 rounded-full border border-[#D2C1AA] bg-white px-5 text-sm text-[#4A3728]"
        >
          Отмена
        </button>
      </div>
    </div>
  );

  const renderVoiceEditor = () => (
    <div
      className="mt-5 rounded-[30px] border border-[#E3D5C3] bg-[#FFFDF9] p-5 md:p-8"
      data-testid="brand-studio-voice-story-v2"
    >
      <div className="flex items-center gap-2 text-[13px] text-[#765D46]">
        <Mic size={15} /> {story ? "Новая версия голосом" : "Первый шаг"}
      </div>
      <h2 className="mt-3 font-serif text-[30px] text-[#2B211A]">
        Расскажи о своём деле голосом
      </h2>
      <p className="mt-2 max-w-2xl text-[13px] leading-5 text-[#806B55]">
        Говори спокойно и своими словами. Можно делать паузы и записать несколько
        фрагментов. Перед сохранением ты обязательно проверишь расшифровку.
      </p>

      <div className="mt-5">
        <VoiceRecorder
          onFragmentsChange={setLocalFragments}
          resetToken={resetToken}
          disabled={busy}
          showRestart={false}
        />
      </div>

      <div className="mt-5 flex flex-wrap gap-3">
        <button
          type="button"
          disabled={
            busy ||
            (!localFragments.length && !voiceSession?.fragments?.length)
          }
          onClick={transcribe}
          className="inline-flex min-h-12 items-center gap-2 rounded-full bg-[#3D2A20] px-6 text-sm text-white disabled:opacity-50"
          data-testid="brand-story-review-voice-v2"
        >
          {busy && <Loader2 size={16} className="animate-spin" />}
          Проверить текст
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={openTextMode}
          className="min-h-12 rounded-full border border-[#D2C1AA] bg-white px-5 text-sm text-[#4A3728]"
        >
          Лучше напишу
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            setMode(null);
            setText(story?.text || "");
            setError("");
          }}
          className="min-h-12 px-3 text-sm text-[#765D44] underline underline-offset-4"
        >
          Отмена
        </button>
      </div>
    </div>
  );

  return (
    <BrandStudioShellV2 activeSpace="create">
      <div
        className="mx-auto max-w-[1120px] pb-14"
        data-testid="brand-studio-create-v2"
      >
        <div className="max-w-3xl">
          <div className="text-[10px] uppercase tracking-[0.24em] text-[#8A6D53]">
            СОЗДАТЬ
          </div>
          <h1 className="mt-2 font-serif text-[38px] leading-[1.02] text-[#2B211A] md:text-[50px]">
            {editMode ? "Изменить бренд" : coreReady ? "Твой бренд готов" : "Создание бренда"}
          </h1>
          <p className="mt-3 max-w-2xl text-[14px] leading-6 text-[#655141]">
            {editMode ? "Измени нужное решение и вернись к своему бренду." : coreReady ? "Твоя визуальная система сохранена." : "Здесь начинается создание бренда. Без анкет и сложных терминов – с твоего рассказа и твоих решений."}
          </p>
        </div>

        {status === "loading" && (
          <div className="mt-8 inline-flex items-center gap-2 text-sm text-[#796652]">
            <Loader2 size={16} className="animate-spin" /> Открываю сохранённые
            данные…
          </div>
        )}

        {status === "error" && (
          <section className="mt-8 rounded-[26px] border border-[#E2CFC8] bg-[#FFF8F4] p-6">
            <h2 className="font-serif text-2xl text-[#2B211A]">
              Не удалось открыть создание бренда
            </h2>
            <p className="mt-2 text-sm leading-6 text-[#6D5842]">
              Сохранённые данные не изменены.
            </p>
            <button
              type="button"
              onClick={load}
              className="mt-4 min-h-11 rounded-full border border-[#CDB899] bg-white px-5 text-sm text-[#4A3728]"
            >
              Повторить
            </button>
          </section>
        )}

        {status === "ready" && story && mode === null && step === "understanding" && (
          <div className="mt-8 space-y-4">
            <button
              type="button"
              onClick={() => goToStep(null)}
              className="min-h-10 text-[12.5px] text-[#765D44] underline underline-offset-4"
            >
              ← Вернуться к рассказу
            </button>
            <BrandUnderstandingStepV2
              story={story}
              onContinue={() => goToStep("business-data")}
            />
          </div>
        )}

        {status === "ready" && story && mode === null && step === "business-data" && (
          <div className="mt-8 space-y-4">
            <button
              type="button"
              onClick={() => goToStep("understanding")}
              className="min-h-10 text-[12.5px] text-[#765D44] underline underline-offset-4"
            >
              ← Вернуться к пониманию
            </button>
            <BrandBusinessDataStepV2 onContinue={editMode ? undefined : () => goToStep("needs")} />
          </div>
        )}

        {status === "ready" && story && mode === null && step === "needs" && (
          <div className="mt-8 space-y-4">
            <button
              type="button"
              onClick={() => goToStep("business-data")}
              className="min-h-10 text-[12.5px] text-[#765D44] underline underline-offset-4"
            >
              ← Вернуться к данным бизнеса
            </button>
            <BrandNeedsStepV2 onContinue={() => goToStep("references")} />
          </div>
        )}

        {status === "ready" && story && mode === null && (step === "references" || step === "typography-anchor") && (
          <div className="mt-8 space-y-4">
            <button
              type="button"
              onClick={() => goToStep("needs")}
              className="min-h-10 text-[12.5px] text-[#765D44] underline underline-offset-4"
            >
              ← Вернуться к задачам
            </button>
            <BrandReferencesWorkspaceV2 onContinue={() => goToStep("visual-style")} />
          </div>
        )}

        {status === "ready" && story && mode === null && step === "visual-style" && (
          <div className="mt-8 space-y-4" data-testid="brand-color-palette-step-v1">
            <button
              type="button"
              onClick={() => goToStep("references")}
              className="min-h-10 text-[12.5px] text-[#765D44] underline underline-offset-4"
            >
              ← Вернуться к визуальным ориентирам
            </button>
            <ColorPaletteCardV1 editMode={editMode} onContinue={() => goToStep("typography")} />
            {params.get("vision-smoke") === "1" && (
              <VisionCapabilityOwnerPanelV1 />
            )}
            {params.get("vision-all-smoke") === "1" && (
              <VisionOrchestrationOwnerPanelV1 />
            )}
            {params.get("synthesis-smoke") === "1" && (
              <VisualSynthesisCapabilityOwnerPanelV1 />
            )}
            {params.get("assembler-smoke") === "1" && (
              <VisualUnderstandingAssemblerOwnerPanelV1 />
            )}
            {params.get("semantic-smoke") === "1" && (
              <CompactSemanticUnderstandingOwnerPanelV1 />
            )}
            {params.get("vd1-smoke") === "1" && (
              <VisualDirectionVnextFoundationOwnerPanelV1 />
            )}
            {params.get("vd2-smoke") === "1" && (
              <VisualDirectionGenerationContractOwnerPanelV1 />
            )}
            {params.get("vd3-smoke") === "1" && (
              <VisualDirectionProviderAdapterOwnerPanelV1 />
            )}
            {params.get("vs1-smoke") === "1" && (
              <StructuredVisualStyleOwnerPanelV1 />
            )}
            {params.get("wp1-smoke") === "1" && (
              <VisualWorkflowPlannerOwnerPanelV1 />
            )}
            {params.get("we1-smoke") === "1" && (
              <VisualWorkflowExecutionOwnerPanelV1 />
            )}
            {params.get("pea1-smoke") === "1" && (
              <VisualProviderExecutionAdapterOwnerPanelV1 />
            )}
            {params.get("pea2-smoke") === "1" && (
              <VisualProviderOwnerExecutionPanelV1 />
            )}
            {params.get("md1-smoke") === "1" && (
              <VisualProviderOwnerMaterialExecutionPanelV1 />
            )}
            {params.get("qg1-smoke") === "1" && (
              <VisualQualityGateOwnerPanelV1 />
            )}
            {params.get("qg12-smoke") === "1" && (
              <VisualQualityEvaluatorPreflightOwnerPanelV1 />
            )}
          </div>
        )}

        {status === "ready" && story && mode === null && step === "typography" && (
          <TypographyCoreStepV1 onBack={() => goToStep("visual-style")} onContinue={editMode ? undefined : () => goToStep("logo")} />
        )}

        {status === "ready" && story && mode === null && step === "logo" && (
          <LogoCoreStepV1 onBack={() => goToStep("typography")} onContinue={editMode ? undefined : () => goToStep("brand-board")} />
        )}

        {status === "ready" && story && mode === null && step === "brand-board" && (
            <BrandBoardCoreV1 onBack={() => goToStep("logo")} onMyBrand={() => navigate("/brand-studio/my-brand")} />
        )}

        {status === "ready" && story && mode === null && step === "visual-search" && (
          <div className="mt-8 space-y-4" data-testid="brand-visual-search-legacy-redirect">
            <button
              type="button"
              onClick={() => goToStep("references")}
              className="min-h-10 text-[12.5px] text-[#765D44] underline underline-offset-4"
            >
              ← Вернуться к визуальным ориентирам
            </button>
            <section className="rounded-[26px] border border-[#E1D4C2] bg-[#FFFDF9] p-5 md:p-8">
              <h2 className="font-serif text-[29px] text-[#2B211A]">
                Этот этап больше не используется
              </h2>
              <p className="mt-2 text-[13px] leading-6 text-[#655141]">
                В новом пути отдельного визуального поиска нет. Вернись к визуальным ориентирам
                и нажми «Собрать мой визуальный стиль».
              </p>
            </section>
          </div>
        )}

        {status === "ready" && story && currentStart === "existing" && context !== "story" && mode === null && (
          <div className="mt-8">
            <ExistingBrandStartV2
              hasStory={Boolean(story)}
              onStoryNeeded={openExistingStory}
            />
          </div>
        )}

        {status === "ready" && story && mode === null && !step && (currentStart !== "existing" || context === "story") && (
          <section className="mt-8 rounded-[30px] border border-[#E3D5C3] bg-[#FFFDF9] p-5 md:p-8" data-testid="brand-story-step-v2">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#EEF0DF] px-3 py-1.5 text-[12px] text-[#586044]">
              <Check size={14} /> Исходный рассказ подтверждён · {versionLabel}
            </div>
            <h2 className="mt-5 font-serif text-[30px] text-[#2B211A]">
              Твой рассказ
            </h2>
            <p className="mt-2 text-[13px] leading-5 text-[#806B55]">
              Это исходная точка бренда. Ты можешь перечитать его и создать новую
              подтверждённую версию — текстом или голосом.
            </p>

            {(view === "story" || !view) && (
              <div className="mt-5 rounded-[24px] border border-[#E7DAC9] bg-[#FAF6EF] p-5 md:p-6">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#927A5B]">
                  ИСХОДНЫЙ РАССКАЗ · {versionLabel}
                </div>
                <p className="mt-4 whitespace-pre-wrap text-[15px] leading-7 text-[#3F3025]">
                  {story.text}
                </p>
              </div>
            )}

            <div className="mt-5 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={openVoiceMode}
                className="inline-flex min-h-12 items-center gap-2 rounded-full bg-[#3D2A20] px-5 text-sm text-[#FFF8ED]"
                data-testid="brand-story-add-voice-v2"
              >
                <Mic size={15} /> Добавить новую версию голосом
              </button>
              <button
                type="button"
                onClick={openTextMode}
                className="inline-flex min-h-12 items-center gap-2 rounded-full border border-[#D2C1AA] bg-white px-5 text-sm text-[#4A3728]"
              >
                <PenLine size={15} /> Изменить рассказ текстом
              </button>
              {currentStart !== "existing" && !coreReady && !editMode && (
                <button
                  type="button"
                  onClick={() => goToStep("understanding")}
                  className="inline-flex min-h-12 items-center gap-2 rounded-full bg-[#3D2A20] px-5 text-sm text-[#FFF8ED]"
                  data-testid="brand-story-continue-v2"
                >
                  Продолжить создание <ArrowRight size={16} />
                </button>
              )}
            </div>
          </section>
        )}

        {status === "ready" && story && mode === "text" && renderTextEditor()}
        {status === "ready" && story && mode === "voice" && renderVoiceEditor()}

        {status === "ready" && !story && (
          <section className="mt-8">
            <div className="grid gap-4 md:grid-cols-2">
              <button
                type="button"
                onClick={() => selectStart("new")}
                className={
                  "rounded-[26px] border p-5 text-left " +
                  (currentStart === "new"
                    ? "border-[#9A7A58] bg-[#F3E9DB]"
                    : "border-[#E1D4C2] bg-[#FFFDF9]")
                }
              >
                <span className="font-serif text-[27px] text-[#2B211A]">
                  Создать бренд
                </span>
                <span className="mt-2 block text-[13px] leading-5 text-[#6B5540]">
                  Начнём с рассказа о твоём деле.
                </span>
              </button>
              <button
                type="button"
                onClick={() => selectStart("existing")}
                className={
                  "rounded-[26px] border p-5 text-left " +
                  (currentStart === "existing"
                    ? "border-[#9A7A58] bg-[#F3E9DB]"
                    : "border-[#E1D4C2] bg-[#FFFDF9]")
                }
              >
                <span className="font-serif text-[27px] text-[#2B211A]">
                  У меня уже есть бренд
                </span>
                <span className="mt-2 block text-[13px] leading-5 text-[#6B5540]">
                  Соберём существующие материалы в систему.
                </span>
              </button>
            </div>

            {(currentStart === "new" || (currentStart === "existing" && context === "story")) && mode === null && (
              <div
                className="mt-5 rounded-[30px] border border-[#E3D5C3] bg-[#FFFDF9] p-5 md:p-8"
                data-testid="brand-story-choice-v2"
              >
                <div className="flex items-center gap-2 text-[13px] text-[#765D46]">
                  <FileText size={15} /> Первый шаг
                </div>
                <h2 className="mt-3 font-serif text-[30px] text-[#2B211A]">
                  Расскажи о своём деле
                </h2>
                <p className="mt-2 max-w-2xl text-[13px] leading-5 text-[#806B55]">
                  Можно рассказать голосом или написать. Профессиональные
                  формулировки не нужны.
                </p>

                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={openVoiceMode}
                    className="rounded-[18px] border border-[#9A7A58] bg-[#F3E9DB] p-5 text-left"
                    data-testid="brand-story-start-voice-v2"
                  >
                    <span className="grid h-10 w-10 place-items-center rounded-full bg-[#3D2A20] text-white">
                      <Mic size={17} />
                    </span>
                    <span className="mt-4 block font-serif text-[24px] text-[#2B211A]">
                      Рассказать голосом
                    </span>
                    <span className="mt-2 block text-[12.5px] leading-5 text-[#6B5540]">
                      Запиши рассказ так, как рассказала бы дизайнеру при первой
                      встрече.
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={openTextMode}
                    className="rounded-[18px] border border-[#D9C9B5] bg-white p-5 text-left"
                    data-testid="brand-story-start-text-v2"
                  >
                    <span className="grid h-10 w-10 place-items-center rounded-full bg-[#EFE4D4] text-[#755D46]">
                      <PenLine size={17} />
                    </span>
                    <span className="mt-4 block font-serif text-[24px] text-[#2B211A]">
                      Написать
                    </span>
                    <span className="mt-2 block text-[12.5px] leading-5 text-[#6B5540]">
                      Напиши своими словами всё, что считаешь важным.
                    </span>
                  </button>
                </div>
              </div>
            )}

            {(currentStart === "new" || (currentStart === "existing" && context === "story")) && mode === "text" && renderTextEditor()}
            {(currentStart === "new" || (currentStart === "existing" && context === "story")) && mode === "voice" && renderVoiceEditor()}

            {currentStart === "existing" && context !== "story" && (
              <div className="mt-5">
                <ExistingBrandStartV2
                  hasStory={Boolean(story)}
                  onStoryNeeded={openExistingStory}
                />
              </div>
            )}
          </section>
        )}

        {error && status === "ready" && (
          <div className="mt-4 rounded-2xl border border-[#E2CFC8] bg-[#FFF8F4] p-4 text-sm text-[#6B443A]">
            {error}
          </div>
        )}

        {(editMode || (coreReady && !step)) && <button type="button" onClick={() => navigate("/brand-studio/my-brand")} className="mt-6 min-h-12 rounded-full bg-[#3D2A20] px-6 text-sm text-[#FFF8ED]" data-testid="brand-edit-return-home">{editMode ? "Вернуться в Мой бренд" : "Открыть Мой бренд"}</button>}

        <footer className="pt-10 pb-2">
          <BrandStudioSignature />
        </footer>
      </div>

      <VoiceReviewWorkspace
        eyebrow="СОЗДАНИЕ БРЕНДА"
        open={reviewOpen}
        session={voiceSession}
        text={text}
        onText={(value) => {
          setText(value);
          setNotice("");
        }}
        busy={busy}
        notice={notice}
        onClose={closeVoiceReview}
        onListen={() => {
          const button = document.querySelector(
            '[data-testid="private-audio-player"] button',
          );
          button?.click();
        }}
        onAdd={() => setReviewOpen(false)}
        onSave={saveVoiceDraft}
        onConfirm={confirmVoiceStory}
        onRestart={restartVoice}
      />
    </BrandStudioShellV2>
  );
}

